import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { CreditorWithDetails } from 'api';

import type { DraftSteps } from '../utils/types';

// This is the actual payload definition of the API
export type DraftStepPayload = { [key: string]: unknown };

type DraftProgressData = Partial<Record<DraftSteps, DraftStepPayload>>;

type DraftProgressContextProps = {
  data: DraftProgressData;
  setData: Dispatch<SetStateAction<DraftProgressData>>;
  draftId: number;

  currentStep: DraftSteps;
  setCurrentStep: Dispatch<SetStateAction<DraftSteps>>;

  validatedSteps: Set<DraftSteps>;
  setValidatedSteps: Dispatch<SetStateAction<Set<DraftSteps>>>;

  selectedReceiver: CreditorWithDetails | undefined;
  setSelectedReceiver: Dispatch<
    SetStateAction<CreditorWithDetails | undefined>
  >;
};

const DraftProgressContext = createContext({} as DraftProgressContextProps);

type DraftProgressProviderProps = {
  children: ReactNode;
  draftId: number;
};

export const DraftProgressProvider = ({
  children,
  draftId
}: DraftProgressProviderProps) => {
  const [data, setData] = useState<DraftProgressData>({});

  const [currentStep, setCurrentStep] = useState<DraftSteps>('payout_request');
  const [validatedSteps, setValidatedSteps] = useState<Set<DraftSteps>>(
    new Set()
  );

  const [selectedReceiver, setSelectedReceiver] = useState<
    CreditorWithDetails | undefined
  >();

  const value = useMemo(
    () => ({
      data,
      setData,
      draftId,
      currentStep,
      setCurrentStep,
      validatedSteps,
      setValidatedSteps,
      selectedReceiver,
      setSelectedReceiver
    }),
    [data, draftId, currentStep, validatedSteps, selectedReceiver]
  );

  return (
    <DraftProgressContext.Provider value={value}>
      {children}
    </DraftProgressContext.Provider>
  );
};

export const useDraftProgress = () => useContext(DraftProgressContext);
